import type { TFunction } from 'i18next'
import {
  PET_CLAIM_ACCEPTED_FILE_TYPES,
  PET_CLAIM_MAX_FILE_BYTES,
  PET_CLAIM_MAX_INVOICES,
  PET_CLAIM_MAX_REQUEST_BYTES,
  PET_CLAIM_PICTURE_TYPES,
} from '@/shared/constants/claims'

// Claims API upload limits, shared by the create and edit claim forms

const BYTES_PER_MB = 1024 * 1024
const MAX_FILE_MB = PET_CLAIM_MAX_FILE_BYTES / BYTES_PER_MB
const MAX_REQUEST_MB = PET_CLAIM_MAX_REQUEST_BYTES / BYTES_PER_MB

const FILE_NAMES_SEPARATOR = ', '

/**
 * Checks files as they are picked, so one that the API would reject (over
 * 10 MB, or a type it doesn't take) never reaches the form. Returns the files
 * that can be added and, when some were left out, a message naming them
 */
export function screenSelectedFiles(
  t: TFunction,
  files: File[],
  acceptedTypes: string[],
): { accepted: File[]; rejectionMessage?: string } {
  const wrongType = files.filter((file) => !acceptedTypes.includes(file.type))
  const tooLarge = files.filter(
    (file) =>
      acceptedTypes.includes(file.type) && file.size > PET_CLAIM_MAX_FILE_BYTES,
  )
  const rejected = new Set([...wrongType, ...tooLarge])
  const names = (list: File[]) =>
    list.map((file) => file.name).join(FILE_NAMES_SEPARATOR)

  const messages = [
    tooLarge.length &&
      t('claims.pet.form.filesTooLargeRejected', {
        files: names(tooLarge),
        max: MAX_FILE_MB,
      }),
    wrongType.length &&
      t('claims.pet.form.filesTypeRejected', { files: names(wrongType) }),
  ].filter((message): message is string => !!message)

  return {
    accepted: files.filter((file) => !rejected.has(file)),
    rejectionMessage: messages.length ? messages.join(' ') : undefined,
  }
}

export function totalBytes(files: (File | undefined)[]): number {
  return files.reduce((sum, file) => sum + (file?.size ?? 0), 0)
}

/**
 * Validates the invoices sent in one request.
 * @param invoiceCount every invoice the claim will end up with (kept + new)
 * @param otherFiles other files sent alongside, counted towards the 40 MB limit
 */
export function validateInvoiceFiles(
  t: TFunction,
  newFiles: File[],
  invoiceCount: number,
  otherFiles: (File | undefined)[],
): string | true {
  if (!invoiceCount) return t('claims.pet.form.receiptsError')
  if (invoiceCount > PET_CLAIM_MAX_INVOICES) {
    return t('claims.pet.form.tooManyFilesError', {
      max: PET_CLAIM_MAX_INVOICES,
    })
  }
  if (
    newFiles.some((file) => !PET_CLAIM_ACCEPTED_FILE_TYPES.includes(file.type))
  ) {
    return t('claims.pet.form.fileTypeError')
  }
  if (newFiles.some((file) => file.size > PET_CLAIM_MAX_FILE_BYTES)) {
    return t('claims.pet.form.fileSizeError', { max: MAX_FILE_MB })
  }
  if (totalBytes([...newFiles, ...otherFiles]) > PET_CLAIM_MAX_REQUEST_BYTES) {
    return t('claims.pet.form.totalSizeError', { max: MAX_REQUEST_MB })
  }
  return true
}

export function validatePetPictureFile(
  t: TFunction,
  file: File | undefined,
): string | true {
  if (!file) return true
  if (!PET_CLAIM_PICTURE_TYPES.includes(file.type)) {
    return t('claims.pet.form.fileTypeError')
  }
  if (file.size > PET_CLAIM_MAX_FILE_BYTES) {
    return t('claims.pet.form.fileSizeError', { max: MAX_FILE_MB })
  }
  return true
}
